import React, { useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import styled from 'styled-components/native'
import useTranslation from '../../../hooks/useTranslation'
import { Block, BlockPropsByType, BlockType } from '../../../doclets/types'
import { TextPreset } from '../../../doclets/blocks/text/presets'
import { builderColors, builderRadii } from './builderColors'
import BlockSettingsPanel from './BlockSettingsPanel'

// Sidebar order and icon for each entry; labels and descriptions are in
// locales under doclets.blocks.<preset or type>. Entries with a preset add a
// Text block pre-filled in that shape (see presets.ts).
export const blockPalette: {
  type: BlockType
  icon: string
  preset?: TextPreset
}[] = [
  { type: 'title', icon: 'T' },
  { type: 'text', icon: '¶' },
  { type: 'image', icon: '▣' },
  { type: 'imageText', icon: '◧' },
  { type: 'media', icon: '▶' },
  // Convenience blocks: Text blocks pre-filled in a common shape
  { type: 'text', preset: 'dateList', icon: '◷' },
  { type: 'text', preset: 'linkList', icon: '↗' },
  { type: 'text', preset: 'labeledSection', icon: '☰' },
  { type: 'text', preset: 'footnote', icon: '※' }
]

interface BlockSidebarProps {
  onAdd: (type: BlockType, preset?: TextPreset) => void
  /** The selected block, whose settings replace the block list. */
  selectedBlock: Block | null
  onToggleHighlight: (id: string) => void
  onToggleFullWidth: (id: string) => void
  onChangeBlock: (id: string, props: BlockPropsByType[BlockType]) => void
  onDeselect: () => void
}

/**
 * The builder's left sidebar: the block list to add from, or -- while a block
 * is selected -- that block's settings. Deselecting always brings the list
 * back.
 */
const BlockSidebar = ({
  onAdd,
  selectedBlock,
  onToggleHighlight,
  onToggleFullWidth,
  onChangeBlock,
  onDeselect
}: BlockSidebarProps) => {
  const { t } = useTranslation()

  if (selectedBlock) {
    return (
      <Container>
        <BlockSettingsPanel
          block={selectedBlock}
          onToggleHighlight={() => onToggleHighlight(selectedBlock.id)}
          onToggleFullWidth={() => onToggleFullWidth(selectedBlock.id)}
          onChange={props => onChangeBlock(selectedBlock.id, props)}
          onDone={onDeselect}
        />
      </Container>
    )
  }

  return (
    <Container>
      <ScrollView contentContainerStyle={{ padding: 16 }}>
        <Heading>{t('doclets:builder:blocks')}</Heading>
        <HelpText>{t('doclets:builder:blocksHelp')}</HelpText>
        {blockPalette.map(({ type, icon, preset }) => (
          <BlockCard
            key={preset ?? type}
            icon={icon}
            label={t(`doclets:blocks:${preset ?? type}:label`)}
            description={t(`doclets:blocks:${preset ?? type}:description`)}
            onPress={() => onAdd(type, preset)}
          />
        ))}
      </ScrollView>
    </Container>
  )
}

interface BlockCardProps {
  icon: string
  label: string
  description: string
  onPress: () => void
}

const BlockCard = ({ icon, label, description, onPress }: BlockCardProps) => {
  const [hovered, setHovered] = useState(false)
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: 12,
        marginBottom: 8,
        borderWidth: 1,
        borderRadius: builderRadii.md,
        borderColor: hovered
          ? builderColors.selectedBorder
          : builderColors.border,
        backgroundColor: '#FFFFFF'
      }}
    >
      <IconBox>
        <IconText>{icon}</IconText>
      </IconBox>
      <View style={{ flex: 1 }}>
        <CardLabel>{label}</CardLabel>
        <CardDescription>{description}</CardDescription>
      </View>
    </Pressable>
  )
}

const Container = styled(View)`
  width: 280px;
  background-color: ${builderColors.sidebar};
  border-right-width: 1px;
  border-right-color: ${builderColors.border};
`

const Heading = styled(Text)`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: ${builderColors.text};
  text-transform: uppercase;
  margin-bottom: 6px;
`

const HelpText = styled(Text)`
  font-size: 12px;
  line-height: 17px;
  color: ${builderColors.mutedText};
  margin-bottom: 12px;
`

const IconBox = styled(View)`
  width: 32px;
  height: 32px;
  margin-right: 12px;
  border-radius: ${builderRadii.md}px;
  align-items: center;
  justify-content: center;
  background-color: ${builderColors.iconFill};
`

const IconText = styled(Text)`
  font-size: 14px;
  color: ${builderColors.text};
`

const CardLabel = styled(Text)`
  font-size: 13px;
  font-weight: 700;
  color: ${builderColors.text};
  margin-bottom: 4px;
`

const CardDescription = styled(Text)`
  font-size: 11px;
  line-height: 15px;
  color: ${builderColors.mutedText};
`

export default BlockSidebar
